import { adicionarLogo } from "../../services/pdf-logo";
// components/recibo-vistoria.tsx
import { useEffect, useState } from "react";
import jsPDF from "jspdf";
import api from "../../services/api";
import { useAppSelector } from "../../store/hooks";
import { formatDate, converterStatusVistoriaParaTexto } from "../../services/utilitarios";
import { Vistoria, StatusVistoria, Locacao } from "../../services/tipos";
import { toast } from "react-toastify";
import { TipoVeiculo } from "../../services/constantes";
import { obterIdTenent, obterNomeEmpresa } from "../../services/seguranca";

interface ReciboVistoriaProps {
  vistoria: Vistoria;
  trigger: number;
}

interface ArquivoImagem {
  nome: string;
  url: string;
  campo: string;
}

export const ReciboVistoria: React.FC<ReciboVistoriaProps> = ({
  vistoria,
  trigger,
}) => {
  const token = useAppSelector((state) => state.token);
  const [carregando, setCarregando] = useState(false);
  const [gerandoPDF, setGerandoPDF] = useState(false);

  // Função auxiliar para garantir que o valor seja uma string válida
  const garantirString = (valor: any): string => {
    if (valor === null || valor === undefined) return 'N/A';
    return String(valor);
  };

  const buscarLocacao = async (idLocacao: string): Promise<Locacao | null> => {
    try {
      const response = await api.get(`/locacao/${idLocacao}`);
      
      if (!response.data.locatario || !response.data.veiculo) {
        throw new Error("Dados da locação incompletos");
      }
      
      return response.data;
    } catch (error) {
      console.error("Erro ao buscar locação:", error);
      toast.error("Não foi possível carregar os dados completos da locação");
      return null;
    }
  };

  const obterLogoDaEmpresa = async (): Promise<string> => {
    try {
      const response = await api.get(
        `Empresa/ObterURLLogo/${obterIdTenent(token)}`
      );
      return response.data;
    } catch (error) {
      return "/images/s.png";
    }
  };

  const carregarImagens = async (vistoriaData: any): Promise<{nome: string, url: string, carregada: boolean, larguraOriginal?: number, alturaOriginal?: number}[]> => {
    if (!vistoriaData) return [];

    const arquivos = getArquivosParaDownload(vistoriaData);
    const imagens: {nome: string, url: string, carregada: boolean, larguraOriginal?: number, alturaOriginal?: number}[] = [];

    for (const arquivo of arquivos) {
      try {
        // Carregar a imagem para obter dimensões originais
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        
        await new Promise((resolve, reject) => {
          img.onload = () => {
            imagens.push({
              nome: arquivo.nome,
              url: arquivo.url,
              carregada: true,
              larguraOriginal: img.naturalWidth,
              alturaOriginal: img.naturalHeight
            });
            resolve(true);
          };
          
          img.onerror = () => {
            console.warn(`Imagem ${arquivo.nome} não acessível`);
            imagens.push({
              nome: arquivo.nome,
              url: arquivo.url,
              carregada: false
            });
            resolve(false);
          };
          
          img.src = arquivo.url;
        });

      } catch (err: any) {
        console.warn(`Erro ao carregar imagem ${arquivo.nome}:`, err);
        imagens.push({
          nome: arquivo.nome,
          url: arquivo.url,
          carregada: false
        });
      }
    }

    return imagens;
  };

  const getArquivosParaDownload = (entidade: any): ArquivoImagem[] => {
    const arquivos: ArquivoImagem[] = [];

    // Verificar quais campos de imagem existem na vistoria
    const camposImagem = [
      { nome: "Foto Frontal", campo: "fotoFrontal" },
      { nome: "Foto Traseira", campo: "fotoTraseira" },
      { nome: "Foto Lateral Esquerda", campo: "fotoLateralEsquerda" },
      { nome: "Foto Lateral Direita", campo: "fotoLateralDireita" },
      { nome: "Foto Pneu Traseiro", campo: "fotoPneuTraseiro" },
      { nome: "Foto Pneu Dianteiro", campo: "fotoPneuDianteiro" },
      { nome: "Foto Painel Ligado", campo: "fotoPainelLigado" },
      { nome: "Foto Frente", campo: "fotoFrente" },
      { nome: "Foto Motor", campo: "fotoMotor" },
      { nome: "Foto Painel", campo: "fotoPainel" },
      { nome: "Foto Porta Malas", campo: "fotoPortaMalas" },
      { nome: "Foto Banco Dianteiro", campo: "fotoBancoDianteiro" },
      { nome: "Foto Banco Traseiro", campo: "fotoBancoTraseiro" },
      { nome: "Foto Pneu Dianteiro Esquerdo", campo: "fotoPneuDianteiroEsquerdo" },
      { nome: "Foto Pneu Dianteiro Direito", campo: "fotoPneuDianteiroDireito" },
      { nome: "Foto Pneu Traseiro Esquerdo", campo: "fotoPneuTraseiroEsquerdo" },
      { nome: "Foto Pneu Traseiro Direito", campo: "fotoPneuTraseiroDireito" },
      { nome: "Foto Hodômetro", campo: "fotoHodometro" },
    ];

    camposImagem.forEach(campo => {
      if (entidade[campo.campo] && entidade[campo.campo].trim() !== '') {
        arquivos.push({
          nome: campo.nome,
          url: entidade[campo.campo],
          campo: campo.campo
        });
      }
    });

    return arquivos;
  };

  const obterTipoVeiculoTexto = (tipoVeiculo: TipoVeiculo) => {
    switch (tipoVeiculo) {
      case TipoVeiculo.tpMoto: return "Moto";
      case TipoVeiculo.tpCarro: return "Carro";
      case TipoVeiculo.tpCaminhao: return "Caminhão";
      default: return "Veículo";
    }
  };

  const obterQuemRealizou = (status: StatusVistoria) => {
    switch (status) {
      case StatusVistoria.FeitoPeloLocador:
        return "Locador (Empresa)";
      case StatusVistoria.EmAnalise:
      case StatusVistoria.Aprovada:
      case StatusVistoria.Recusada:
        return "Locatário";
      default:
        return "Sistema";
    }
  };

  const calcularDimensoesImagem = (larguraOriginal: number, alturaOriginal: number, larguraMaxima: number, alturaMaxima: number) => {
    const ratioLargura = larguraMaxima / larguraOriginal;
    const ratioAltura = alturaMaxima / alturaOriginal;
    const ratio = Math.min(ratioLargura, ratioAltura);
    
    return {
      largura: larguraOriginal * ratio,
      altura: alturaOriginal * ratio
    };
  };

  const adicionarImagensAoPDF = async (pdf: jsPDF, posY: number, dimensoes: any, paleta: any, imagensCarregadas: any[]): Promise<number> => {
    if (imagensCarregadas.length === 0) {
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.setTextColor(paleta.secundaria);
      pdf.text("Nenhuma imagem disponível para exibição", dimensoes.margem, posY);
      return posY + 10;
    }

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(12);
    pdf.setTextColor(paleta.primaria);
    pdf.text("REGISTROS FOTOGRÁFICOS", dimensoes.margem, posY);
    posY += 8;

    const larguraMaxima = (dimensoes.larguraUtil - 10) / 2;
    const alturaMaxima = 60;

    let imagensAdicionadas = 0;

    for (let i = 0; i < imagensCarregadas.length; i++) {
      const coluna = i % 2;
      const x = dimensoes.margem + (coluna * (larguraMaxima + 10));

      try {
        if (imagensCarregadas[i].carregada && imagensCarregadas[i].larguraOriginal && imagensCarregadas[i].alturaOriginal) {
          // Calcular dimensões mantendo proporção original
          const dimensoesCalculadas = calcularDimensoesImagem(
            imagensCarregadas[i].larguraOriginal,
            imagensCarregadas[i].alturaOriginal,
            larguraMaxima,
            alturaMaxima
          );

          // Verificar se precisa de nova página considerando a altura calculada
          if (posY + dimensoesCalculadas.altura + 25 > dimensoes.alturaPagina - dimensoes.margem) {
            pdf.addPage();
            posY = dimensoes.margem;
          }

          await new Promise<void>((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            
            img.onload = () => {
              try {
                // Adicionar imagem com dimensões proporcionais
                pdf.addImage(img, 'JPEG', x, posY, dimensoesCalculadas.largura, dimensoesCalculadas.altura);
                imagensAdicionadas++;
                
                // Adicionar título da imagem
                pdf.setFontSize(8);
                pdf.setTextColor(paleta.secundaria);
                const titulo = pdf.splitTextToSize(garantirString(imagensCarregadas[i].nome), larguraMaxima);
                
                let linhasTitulo = 1;
                if (Array.isArray(titulo)) {
                  titulo.forEach((linha: string, index: number) => {
                    pdf.text(linha, x, posY + dimensoesCalculadas.altura + 4 + (index * 3));
                  });
                  linhasTitulo = titulo.length;
                } else {
                  pdf.text(titulo, x, posY + dimensoesCalculadas.altura + 4);
                }

                // Se for a segunda coluna, avançar para próxima linha
                if (coluna === 1) {
                  const maiorAltura = Math.max(dimensoesCalculadas.altura, (i-1 >= 0 && imagensCarregadas[i-1].carregada && imagensCarregadas[i-1].alturaOriginal) 
                    ? calcularDimensoesImagem(
                        imagensCarregadas[i-1].larguraOriginal, 
                        imagensCarregadas[i-1].alturaOriginal, 
                        larguraMaxima, 
                        alturaMaxima
                      ).altura 
                    : dimensoesCalculadas.altura);
                  posY += maiorAltura + 10 + (linhasTitulo * 3);
                }

                // Se for o último item e estiver na primeira coluna, avançar para próxima linha
                if (i === imagensCarregadas.length - 1 && coluna === 0) {
                  posY += dimensoesCalculadas.altura + 10 + (linhasTitulo * 3);
                }

                resolve();
              } catch (error) {
                console.error(`Erro ao adicionar imagem ${imagensCarregadas[i].nome} ao PDF:`, error);
                reject(error);
              }
            };

            img.onerror = (error) => {
              console.warn(`Erro ao carregar imagem ${imagensCarregadas[i].nome}:`, error);
              // Adicionar placeholder para imagem que não carregou
              pdf.setFillColor(240, 240, 240);
              pdf.rect(x, posY, larguraMaxima, 40, 'F');
              pdf.setFontSize(8);
              pdf.setTextColor(paleta.secundaria);
              pdf.text('Imagem não disponível', x + 5, posY + 20);
              
              // Adicionar título
              const titulo = pdf.splitTextToSize(garantirString(imagensCarregadas[i].nome), larguraMaxima);
              let linhasTitulo = 1;
              if (Array.isArray(titulo)) {
                titulo.forEach((linha: string, index: number) => {
                  pdf.text(linha, x, posY + 44 + (index * 3));
                });
                linhasTitulo = titulo.length;
              } else {
                pdf.text(titulo, x, posY + 44);
              }

              if (coluna === 1) {
                posY += 40 + 10 + (linhasTitulo * 3);
              }

              if (i === imagensCarregadas.length - 1 && coluna === 0) {
                posY += 40 + 10 + (linhasTitulo * 3);
              }

              resolve();
            };

            img.src = imagensCarregadas[i].url;
          });
        } else {
          // Para imagens não carregadas, usar dimensões padrão
          if (posY + 40 + 25 > dimensoes.alturaPagina - dimensoes.margem) {
            pdf.addPage();
            posY = dimensoes.margem;
          }

          // Adicionar placeholder
          pdf.setFillColor(240, 240, 240);
          pdf.rect(x, posY, larguraMaxima, 40, 'F');
          pdf.setFontSize(8);
          pdf.setTextColor(paleta.secundaria);
          pdf.text('Imagem não disponível', x + 5, posY + 20);
          
          // Adicionar título
          const titulo = pdf.splitTextToSize(garantirString(imagensCarregadas[i].nome), larguraMaxima);
          let linhasTitulo = 1;
          if (Array.isArray(titulo)) {
            titulo.forEach((linha: string, index: number) => {
              pdf.text(linha, x, posY + 44 + (index * 3));
            });
            linhasTitulo = titulo.length;
          } else {
            pdf.text(titulo, x, posY + 44);
          }

          if (coluna === 1) {
            posY += 40 + 10 + (linhasTitulo * 3);
          }

          if (i === imagensCarregadas.length - 1 && coluna === 0) {
            posY += 40 + 10 + (linhasTitulo * 3);
          }
        }
      } catch (error) {
        console.error(`Erro ao processar imagem ${imagensCarregadas[i].nome}:`, error);
      }
    }

    return posY;
  };

  const gerarConteudoPDF = async (pdf: jsPDF, locacaoData: Locacao | null, imagensCarregadasData: any[], nomeEmpresa: string) => {
    const paleta = {
      primaria: "#1A202C",
      secundaria: "#4A5568",
      destaque: "#1F467E",
      borda: "#E2E8F0",
      sucesso: "#4CAF50",
      alerta: "#FFC107",
      erro: "#f44336"
    };

    const dimensoes = {
      larguraPagina: pdf.internal.pageSize.width,
      alturaPagina: pdf.internal.pageSize.height,
      margem: 15,
      larguraUtil: pdf.internal.pageSize.width - (15 * 2)
    };

    let posY = dimensoes.margem;

    // Adicionar logo primeiro (antes da linha azul)
    let logoAdicionada = false;
    try {
      const logoUrl = await obterLogoDaEmpresa();
      if (logoUrl && logoUrl !== "/images/s.png") {
        const img = new Image();
        img.src = logoUrl;
        
        await new Promise((resolve) => {
          img.onload = () => {
            try {
              adicionarLogo(pdf, img, {
                x: pdf.internal.pageSize.width - 55,
                y: 10,
                largura: 40,
                altura: 25,
                alinhar: "direita",
              });
              logoAdicionada = true;
              resolve(true);
            } catch (error) {
              console.error("Erro ao adicionar logo:", error);
              resolve(false);
            }
          };
          img.onerror = () => resolve(false);
        });
      }
    } catch (error) {
      console.error("Erro ao processar logo:", error);
    }

    // Título principal
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor(paleta.primaria);
    pdf.text("RELATÓRIO DE VISTORIA", dimensoes.margem, posY);
    posY += 8;

    // Nome da empresa abaixo do título (CORREÇÃO 3)
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(12);
    pdf.setTextColor(paleta.destaque);
    pdf.text(nomeEmpresa, dimensoes.margem, posY);
    posY += 10;

    // Informações da Vistoria
    pdf.setFontSize(12);
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Código da Vistoria: ${garantirString(vistoria.id?.toUpperCase())}`, dimensoes.margem, posY);
    posY += 7;
    
    const dataVistoria = vistoria.data ? formatDate(garantirString(vistoria.data)) : 'N/A';
    pdf.text(`Data da Vistoria: ${dataVistoria}`, dimensoes.margem, posY);
    posY += 7;
    
    const status = converterStatusVistoriaParaTexto(vistoria.status);
    let corStatus = paleta.secundaria;
    if (vistoria.status === StatusVistoria.Aprovada) corStatus = paleta.sucesso;
    else if (vistoria.status === StatusVistoria.Recusada) corStatus = paleta.erro;
    else if (vistoria.status === StatusVistoria.EmAnalise) corStatus = paleta.alerta;

    pdf.setTextColor(corStatus);
    pdf.text(`Status: ${garantirString(status)}`, dimensoes.margem, posY);
    posY += 7;
    
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Realizada por: ${garantirString(obterQuemRealizou(vistoria.status))}`, dimensoes.margem, posY);
    posY += 12;

    // Informações do Veículo
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(paleta.primaria);
    pdf.text("INFORMAÇÕES DO VEÍCULO", dimensoes.margem, posY);
    posY += 8;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    
    const infoVeiculo = [
      { 
        label: "Placa:", 
        value: garantirString(locacaoData?.veiculo?.placa) 
      },
      { 
        label: "Marca/Modelo:", 
        value: `${garantirString(locacaoData?.veiculo?.marca)} ${garantirString(locacaoData?.veiculo?.modelo)}` 
      },
      { 
        label: "Ano:", 
        value: garantirString(locacaoData?.veiculo?.ano) 
      },
      { 
        label: "Tipo:", 
        value: obterTipoVeiculoTexto(locacaoData?.veiculo?.tipoVeiculo || TipoVeiculo.tpNenhum) 
      },
      { 
        label: "Hodômetro:", 
        value: garantirString(vistoria.hodometro) 
      },
    ];

    infoVeiculo.forEach((info) => {
      pdf.text(garantirString(info.label), dimensoes.margem, posY);
      pdf.text(garantirString(info.value), dimensoes.margem + 35, posY);
      posY += 5;
    });
    posY += 8;

    // Informações do Locatário
    if (locacaoData?.locatario) {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(11);
      pdf.text("LOCATÁRIO", dimensoes.margem, posY);
      posY += 6;

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text(`Nome: ${garantirString(locacaoData.locatario.nome)}`, dimensoes.margem, posY);
      posY += 5;
      pdf.text(`Documento: ${garantirString(locacaoData.locatario.documento)}`, dimensoes.margem, posY);
      posY += 10;
    }

    // Observações
    if (vistoria.obs) {
      pdf.setFont("helvetica", "bold");
      pdf.text("OBSERVAÇÕES", dimensoes.margem, posY);
      posY += 6;

      pdf.setFont("helvetica", "normal");
      const observacoes = pdf.splitTextToSize(garantirString(vistoria.obs), dimensoes.larguraUtil);
      if (Array.isArray(observacoes)) {
        observacoes.forEach((linha: string) => {
          pdf.text(linha, dimensoes.margem, posY);
          posY += 4;
        });
      } else {
        pdf.text(observacoes, dimensoes.margem, posY);
        posY += 4;
      }
      posY += 6;
    }

    // Adicionar imagens
    posY = await adicionarImagensAoPDF(pdf, posY, dimensoes, paleta, imagensCarregadasData);

    // Rodapé
    const rodapeY = dimensoes.alturaPagina - 15;
    pdf.setFontSize(8);
    pdf.setTextColor(paleta.secundaria);
    pdf.text(`Emitido em: ${new Date().toLocaleString('pt-BR')}`, dimensoes.larguraPagina - dimensoes.margem - 40, rodapeY);
  };

  const gerarPDFVistoria = async () => {
    if (!vistoria) {
      toast.error("Dados da vistoria não disponíveis");
      return;
    }

    if (gerandoPDF) {
      return;
    }

    setGerandoPDF(true);
    setCarregando(true);

    try {
      // Buscar dados da locação
      let locacaoData = null;
      if (vistoria.idLocacao) {
        locacaoData = await buscarLocacao(vistoria.idLocacao);
      }

      // Nome ATUAL da empresa: o do token é o do login e fica velho quando a empresa é renomeada.
      // Sem permissão ou sem rede, cai no do token.
      let nomeEmpresa = obterNomeEmpresa(token);
      try {
        const respostaEmpresa = await api.get(`Empresa/${obterIdTenent(token)}`);
        if (respostaEmpresa.data?.nome) nomeEmpresa = respostaEmpresa.data.nome;
      } catch {
        // mantém o nome do token
      }

      // Carregar informações das imagens com dimensões originais
      const imagensCarregadasData = await carregarImagens(vistoria);

      // Criar PDF
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Gerar conteúdo do PDF com os dados carregados
      await gerarConteudoPDF(pdf, locacaoData, imagensCarregadasData, nomeEmpresa);

      // Gerar e abrir PDF
      const pdfBlob = pdf.output("blob");
      const url = URL.createObjectURL(pdfBlob);
      const id = `vistoria_${vistoria.id}`;
      localStorage.setItem(`pdfURL_${id}`, url);
      window.open(`/pdf-viewer?id=${id}`, "_blank");
      
      toast.success("PDF gerado com sucesso!");
      
    } catch (error) {
      console.error("Erro ao gerar PDF:", error);
      toast.error("Erro ao gerar PDF da vistoria");
    } finally {
      setGerandoPDF(false);
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (trigger > 0 && vistoria && !gerandoPDF) {
      gerarPDFVistoria();
    }
  }, [trigger, vistoria]);

  if (carregando) {
    return <div>Gerando relatório da vistoria...</div>;
  }

  return null;
};